import React from 'react';
import {
  default as MuiSlider,
  type SliderTypeMap as MuiSliderTypeMap,
  type SliderProps as MuiSliderProps,
} from '@mui/material/Slider';
import type {
  OverridableComponent,
  OverrideProps,
} from '@mui/material/OverridableComponent';
import { DistributiveOmit } from '@mui/types';

/**
 * Props that we do not want to include with Slider.
 *
 * Using `keyof Pick` provides type safety that ensures we are
 * only blocking props that are part of the interface.
 **/
type SliderBlockedProps = keyof Pick<
  MuiSliderProps,
  'color' | 'orientation' | 'track'
>;

export interface SliderTypeMap<
  // eslint-disable-next-line @typescript-eslint/ban-types
  AdditionalProps = {},
  RootComponent extends React.ElementType = MuiSliderTypeMap['defaultComponent']
> {
  props: AdditionalProps & DistributiveOmit<MuiSliderProps, SliderBlockedProps>;
  defaultComponent: RootComponent;
}

export type SliderProps<
  RootComponent extends React.ElementType = SliderTypeMap['defaultComponent'],
  // eslint-disable-next-line @typescript-eslint/ban-types
  AdditionalProps = {}
> = OverrideProps<
  SliderTypeMap<AdditionalProps, RootComponent>,
  RootComponent
> & {
  component?: React.ElementType;
};

export const Slider: OverridableComponent<SliderTypeMap> = MuiSlider;
